import { Address } from "@/services/backend/addresses/types/address";
import t from "@/lang/lang";
import { useState } from "react";
import {
  RtkQueryMutation,
  useDebouncedMutation,
} from "@/shared/lib/debounce/debounce";
import { FieldErrors } from "@/shared/lib/errors/validation-error";
import { joinFieldName } from "@/shared/lib/errors/err-validation-failed-context";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { TextInputField } from "@/shared/components/field/input-field";

type Mutation<Req, Res> = () => RtkQueryMutation<Req, Res>;

export interface PostalAddressCardProps {
  address: Address;
  onAddressChange: (address: Address) => void;
  title?: string;
  description?: string;
  /**
   * Additional errors, keyed relative to the address (e.g. "street"),
   * merged with the errors of the ErrValidationFailedContext.
   */
  fieldErrors?: FieldErrors;
  /**
   * The field name of the address in the ErrValidationFailedContext. Its fields
   * are read from and reset at `${fieldName}.<field>`, e.g.
   * "postalAddress.street".
   */
  fieldName?: string;
  disabled?: boolean;
}

export function PostalAddressCard({
  address,
  onAddressChange,
  fieldErrors = undefined,
  fieldName = "postalAddress",
  title = t("Postadresse"),
  description = t("Adresse des Kunden"),
  disabled = false,
}: PostalAddressCardProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4 sm:grid-cols-2">
        <TextInputField
          label={t("Firma")}
          text={address.company}
          onTextChange={(company) => onAddressChange({ ...address, company })}
          fieldName={joinFieldName(fieldName, "company")}
          errors={fieldErrors?.company}
          disabled={disabled}
        />
        <TextInputField
          label={t("Abteilung")}
          text={address.department}
          onTextChange={(department) =>
            onAddressChange({ ...address, department })
          }
          fieldName={joinFieldName(fieldName, "department")}
          errors={fieldErrors?.department}
          disabled={disabled}
        />
        <TextInputField
          label={t("Ansprechpartner")}
          text={address.contact}
          onTextChange={(contact) => onAddressChange({ ...address, contact })}
          fieldName={joinFieldName(fieldName, "contact")}
          errors={fieldErrors?.contact}
          disabled={disabled}
        />
        <TextInputField
          label={t("Straße & Hausnummer")}
          text={address.street}
          onTextChange={(street) => onAddressChange({ ...address, street })}
          fieldName={joinFieldName(fieldName, "street")}
          errors={fieldErrors?.street}
          disabled={disabled}
        />
        <div className="grid grid-cols-4 gap-4">
          <TextInputField
            label={t("PLZ")}
            text={address.postalCode}
            onTextChange={(postalCode) =>
              onAddressChange({ ...address, postalCode })
            }
            fieldName={joinFieldName(fieldName, "postalCode")}
            errors={fieldErrors?.postalCode}
            disabled={disabled}
          />
          <TextInputField
            label={t("Stadt")}
            text={address.city}
            onTextChange={(city) => onAddressChange({ ...address, city })}
            fieldName={joinFieldName(fieldName, "city")}
            errors={fieldErrors?.city}
            disabled={disabled}
            fieldProps={{ className: "col-span-3" }}
          />
        </div>
        <TextInputField
          label={t("Land")}
          text={address.country}
          onTextChange={(country) => onAddressChange({ ...address, country })}
          fieldName={joinFieldName(fieldName, "country")}
          errors={fieldErrors?.country}
          disabled={disabled}
        />
      </CardContent>
    </Card>
  );
}

interface PostalAddressCardWithMutationProps<Req, Res> {
  id: string;
  address: Address;
  mutation: Mutation<Req, Res>;
  title?: string;
  description?: string;
  /** See {@link PostalAddressCard}. */
  fieldErrors?: FieldErrors;
  /** See {@link PostalAddressCard}. Defaults to "postalAddress". */
  fieldName?: string;
  disabled?: boolean;
}

/**
 * PostalAddressCardWithMutation is a {@link PostalAddressCard} saving the
 * address with a PUT of `{ id, postalAddress }`. Its validation errors are
 * written into the nearest ErrValidationFailedContextProvider, which the page
 * mounts.
 */
export function PostalAddressCardWithMutation<
  Req extends { id: string; postalAddress: Address },
  Res,
>({
  id,
  address,
  mutation,
  title = t("Postadresse"),
  description = t(
    "Adresse des Kunden, wie sie auf dem Angebot erscheinen soll",
  ),
  fieldErrors = undefined,
  fieldName = "postalAddress",
  disabled = false,
}: PostalAddressCardWithMutationProps<Req, Res>) {
  // @ts-expect-error - I do not understand the error. But it works.
  const [request, setRequest] = useState<Req>({ id, postalAddress: address });
  const debouncedMutation = mutation();
  useDebouncedMutation(
    request,
    setRequest,
    debouncedMutation,
    // @ts-expect-error - I do not understand the error. But it works.
    { id, postalAddress: address },
  );

  return (
    <PostalAddressCard
      address={request.postalAddress}
      // @ts-expect-error - I do not understand the error. But it works.
      onAddressChange={(a) => setRequest({ id, postalAddress: a })}
      title={title}
      description={description}
      fieldErrors={fieldErrors}
      fieldName={fieldName}
      disabled={disabled}
    />
  );
}
